import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Send, Paperclip } from 'lucide-react';
import { FilePreview } from './FilePreview';
import { useTranslation } from '../hooks/useTranslation';
import type { WidgetConfig } from '../types';

interface InputBarProps {
  config: WidgetConfig;
  disabled: boolean;
  onSend: (text: string, file?: File) => void;
}

export const InputBar: React.FC<InputBarProps> = ({ config, disabled, onSend }) => {
  const { t } = useTranslation(config.behavior?.locale);
  const [text, setText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [errorToast, setErrorToast] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-dismiss toast
  useEffect(() => {
    if (!errorToast) return;
    const timer = setTimeout(() => setErrorToast(null), 4000);
    return () => clearTimeout(timer);
  }, [errorToast]);

  // Auto-grow textarea to fit content
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 96) + 'px'; // 96px = max-h-24
  }, [text]);

  const handleSend = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed && !selectedFile) return;

    onSend(trimmed, selectedFile || undefined);
    setText('');
    setSelectedFile(null);

    // Keep focus on textarea for next message (especially on mobile)
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [text, selectedFile, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const maxMb = config.conversation.file_upload_max_mb;
      if (file.size > maxMb * 1024 * 1024) {
        setErrorToast(t('file.tooLarge', { maxMb }));
        return;
      }

      const allowed = config.conversation.allowed_file_types;
      if (allowed.length > 0) {
        const ext = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!allowed.includes(ext) && !allowed.includes(file.type)) {
          setErrorToast(t('file.typeNotAllowed'));
          return;
        }
      }

      setSelectedFile(file);
      // Reset input so same file can be selected again
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [config]
  );

  return (
    <div className="shrink-0 border-t border-gray-100 bg-white relative" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      {errorToast && (
        <div className="absolute bottom-full left-0 right-0 flex justify-center pb-2 px-3 pointer-events-none">
          <span className="pointer-events-auto text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-1.5 rounded-lg shadow-sm animate-fade-in">
            {errorToast}
          </span>
        </div>
      )}
      {selectedFile && (
        <FilePreview file={selectedFile} onRemove={() => setSelectedFile(null)} />
      )}
      <div className="flex items-end gap-2 px-3 py-2">
        {config.conversation.file_upload_enabled && (
          <>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              className="flex items-center justify-center min-w-[44px] min-h-[44px] text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40"
              aria-label={t('chat.attachFile')}
            >
              <Paperclip size={18} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileSelect}
              accept={config.conversation.allowed_file_types.join(',')}
            />
          </>
        )}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={(e) => {
            // Scroll input into view when mobile keyboard opens
            requestAnimationFrame(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'end' }));
          }}
          disabled={disabled}
          placeholder={disabled ? t('chat.offline') : config.behavior.placeholder_text}
          enterKeyHint="send"
          autoComplete="off"
          className="flex-1 resize-none text-sm bg-gray-50 rounded-xl px-3 py-2 max-h-24 overflow-y-auto focus:outline-none focus:ring-1 focus:ring-gray-300 disabled:opacity-40 placeholder:text-gray-400"
          rows={1}
          aria-label="Message input"
          style={{ fontFamily: config.branding.font_family || 'system-ui, sans-serif' }}
        />
        <button
          onClick={handleSend}
          disabled={disabled || (!text.trim() && !selectedFile)}
          className="flex items-center justify-center min-w-[44px] min-h-[44px] rounded-full transition-colors disabled:opacity-40"
          style={{
            backgroundColor: config.branding.primary_color,
            color: '#ffffff',
          }}
          aria-label={t('chat.sendMessage')}
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  );
};
